/**
 * The owner's note when a long run of shifts has no health answer but "all clear" (core zeroreports.ts). Two buttons:
 * "Remind the team" sends the same neutral reminder to everyone on the team through the existing reminder route (the person
 * sees it on their own Today), and "Not now" hides the note for two weeks. It shows two counts and names no one.
 */
import { useState } from "react";
import { ZERO_REPORT_WORDS as W, zeroReportSnoozedUntil, type ZeroReportNote } from "@logwave/core";
import { Button, useToast } from "./ui.tsx";
import { useApi, useSession } from "../session.tsx";
import { writeZeroSnooze } from "../lib/zeroReports.ts";

export function ZeroReportsBody({ note, today, onDone }: { note: ZeroReportNote; today: string; onDone(): void }) {
  const api = useApi();
  const s = useSession();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  async function remind() {
    setBusy(true);
    try {
      const team = (await api.employees(s.org.id)).filter((e) => e.active && e.id !== s.employeeId && (e.locationIds.length === 0 || e.locationIds.includes(s.location.id)));
      const sent = await Promise.allSettled(team.map((e) => api.remind(s.org.id, e.id, W.message)));
      if (team.length > 0 && sent.every((r) => r.status === "rejected")) throw new Error("Couldn't send the reminder. Try again.");
      writeZeroSnooze(s.org.id, s.location.id, zeroReportSnoozedUntil(today));
      toast.show(W.sent, { icon: "check" });
      onDone();
    } catch (x) { toast.show(x instanceof Error ? x.message : "Couldn't send the reminder. Try again.", { icon: "alert" }); } finally { setBusy(false); }
  }
  function notNow() { writeZeroSnooze(s.org.id, s.location.id, zeroReportSnoozedUntil(today)); onDone(); }
  return (
    <div className="stack-sm" data-testid="zero-reports">
      <h2 className="fcard-title">{W.title(note.days)}</h2>
      <p>{W.body}</p>
      <div className="btn-row">
        <Button block icon="bell" busy={busy} onClick={() => void remind()} data-testid="zero-reports-remind">{W.remind}</Button>
        <Button block variant="quiet" disabled={busy} onClick={notNow} data-testid="zero-reports-not-now">{W.notNow}</Button>
      </div>
      <p className="caption">{W.caption}</p>
    </div>
  );
}
